import React, { useState, useEffect } from 'react';
import {
  Descriptions,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Progress,
  Timeline,
  Button,
  Space,
  Table,
  message,
  Switch,
  Select
} from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";

const { Title, Text } = Typography;

interface OnboardingDetailsProps {
  process: any;
  onRefresh: () => void;
}

const OnboardingDetails: React.FC<OnboardingDetailsProps> = ({ process, onRefresh }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [buddySelectValue, setBuddySelectValue] = useState<number | null>(process.buddy_assigned_id || null);
  const [savingBuddy, setSavingBuddy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTasks();
    apiService.getEmployees().then((r: any) => { if (r.success) setEmployees(r.data || []); });
  }, [process.id]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOnboardingTasks(process.id);
      if (response.success) {
        setTasks(response.data || []);
      }
    } catch (error) {
      message.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleBuddySave = async () => {
    setSavingBuddy(true);
    const r = await apiService.updateOnboardingProcess(process.id, { buddy_assigned_id: buddySelectValue });
    setSavingBuddy(false);
    if (r.success) { message.success('Buddy assigned successfully'); onRefresh(); }
    else message.error(r.error || 'Failed to assign buddy');
  };

  const handleTaskStatusChange = async (taskId: string, completed: boolean) => {
    try {
      const response = await apiService.updateOnboardingTask(taskId, {
        status: completed ? 'completed' : 'pending',
        completed_date: completed ? new Date().toISOString().split('T')[0] : null
      });

      if (response.success) {
        message.success('Task updated successfully');
        fetchTasks();
        onRefresh();
      } else {
        message.error('Failed to update task');
      }
    } catch (error) {
      message.error('Failed to update task');
    }
  };

  const formatDate = (date: string) => {
    return date ? new Date(date).toLocaleDateString() : 'Not set';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'processing';
      case 'pending': return 'warning';
      case 'on_hold': return 'error';
      default: return 'default';
    }
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'processing';
      case 'pending': return 'warning';
      case 'skipped': return 'default';
      default: return 'default';
    }
  };

  const taskColumns = [
    {
      title: 'Task',
      dataIndex: 'task_name',
      key: 'task_name',
      render: (name: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color="blue">{(category || '').replace('_', ' ').toUpperCase()}</Tag>
      )
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      render: (assignedTo: string) => (
        <Tag color="purple">{(assignedTo || '').replace('_', ' ').toUpperCase()}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getTaskStatusColor(status)}>
          {(status || '').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={priority === 'high' ? 'red' : priority === 'medium' ? 'orange' : 'green'}>
          {(priority || '').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date: string) => formatDate(date)
    },
    {
      title: 'Complete',
      key: 'complete',
      render: (record: any) => (
        <Switch
          checked={record.status === 'completed'}
          onChange={(checked) => handleTaskStatusChange(record.task_id, checked)}
          checkedChildren="Done"
          unCheckedChildren="Pending"
        />
      )
    }
  ];

  const completionPercentage = parseFloat(process.completion_percentage);

  return (
    <div>
      {/* Header Section */}
      <Card style={{ marginBottom: '16px' }}>
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              {process.employee_name}
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {process.department} • {process.position}
            </Text>
            <br />
            <Space style={{ marginTop: '8px' }}>
              <Tag color={getStatusColor(process.onboarding_status)}>
                {(process.onboarding_status || '').replace('_', ' ').toUpperCase()}
              </Tag>
              {process.buddy_name && (
                <Tag color="blue" icon={<TeamOutlined />}>
                  Buddy: {process.buddy_name}
                </Tag>
              )}
            </Space>
          </Col>
          <Col>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={completionPercentage}
                size={80}
                strokeColor={completionPercentage >= 80 ? '#52c41a' : completionPercentage >= 50 ? '#faad14' : '#1890ff'}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Process Details */}
      <Card title="Onboarding Details" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Employee ID">
            {process.employee_id}
          </Descriptions.Item>
          <Descriptions.Item label="Start Date">
            <Space>
              <CalendarOutlined />
              {formatDate(process.start_date)}
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Days Since Start">
            {process.days_since_start} days
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(process.onboarding_status)}>
              {(process.onboarding_status || '').replace('_', ' ').toUpperCase()}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label="Orientation Completed">
            {process.orientation_completed ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
            ) : (
              <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Orientation Date">
            {formatDate(process.orientation_date)}
          </Descriptions.Item>
          
          <Descriptions.Item label="Documents Submitted">
            {process.documents_submitted ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
            ) : (
              <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Equipment Assigned">
            {process.equipment_assigned ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
            ) : (
              <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
            )}
          </Descriptions.Item>
          
          <Descriptions.Item label="System Access">
            {process.system_access_granted ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Granted</Tag>
            ) : (
              <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Buddy Assigned">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select
                style={{ width: 200 }}
                value={buddySelectValue}
                onChange={setBuddySelectValue}
                placeholder="Select buddy"
                allowClear
              >
                {employees.filter((e: any) => e.id !== process.employee_id).map((e: any) => (
                  <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Select.Option>
                ))}
              </Select>
              <Button size="small" type="primary" loading={savingBuddy} onClick={handleBuddySave}>Save</Button>
            </div>
            {process.buddy_name ? (
              <Space>
                <UserOutlined />
                {process.buddy_name}
              </Space>
            ) : (
              <Tag color="default">Not Assigned</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Task Summary */}
      <Card title="Task Summary" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                {process.task_summary.total_tasks}
              </div>
              <div style={{ color: '#666' }}>Total Tasks</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                {process.task_summary.completed_tasks}
              </div>
              <div style={{ color: '#666' }}>Completed</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                {process.task_summary.overdue_tasks}
              </div>
              <div style={{ color: '#666' }}>Overdue</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                {process.task_summary.pending_overdue}
              </div>
              <div style={{ color: '#666' }}>Pending</div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Tasks Table */}
      <Card title="Onboarding Tasks">
        <Table
          columns={taskColumns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      {/* Notes */}
      {process.notes && (
        <Card title="Notes" style={{ marginTop: '16px' }}>
          <Text>{process.notes}</Text>
        </Card>
      )}
    </div>
  );
};

export default OnboardingDetails;
